import CheckBoxIcon from '@mui/icons-material/CheckBox';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import type { CheckboxProps, SelectProps, SwitchProps, TextFieldProps } from '@mui/material';
import {
  Autocomplete,
  Checkbox,
  FormControl,
  FormControlLabel,
  FormHelperText,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import Grid from '@mui/material/Grid';
import { captureMessage } from '@sentry/react';
import { isEmpty } from 'lodash-es';
import type { Control, FieldErrors, FieldValues, Path, SubmitErrorHandler } from 'react-hook-form';
import { Controller } from 'react-hook-form';

import CurrencySymbol from '~/components/common/CurrencySymbol';
import LineItems from '~/components/invoices/LineItems';
import { LiftAPIClientError } from '~/data/liftApiMutator';
import { validateMaskHHmm } from '~/data/schemas/zodHelpers';

interface FieldOption {
  id: string;
  label: string;
}

interface FieldDefBase<FieldProps = {}> {
  gridProps?: { xs?: number; sm?: number; md?: number; lg?: number; xl?: number };
  fieldProps?: Omit<FieldProps, 'type' | 'name' | 'label' | 'required' | 'value'>;
  type: string;
  name: string;
  required?: boolean;
  disabled?: boolean;
  options?: Array<{ id: string; label: string }>;
  InputProps?: TextFieldProps['InputProps'];
}

const flattenErrorData = (data: unknown, prefix = ''): string[] => {
  if (typeof data === 'string') return prefix ? [`${prefix} -> ${data}`] : [data];
  if (Array.isArray(data)) return data.flatMap((item) => flattenErrorData(item, prefix));
  if (data !== null && typeof data === 'object') {
    return Object.entries(data as Record<string, unknown>).flatMap(([key, value]) =>
      flattenErrorData(value, prefix ? `${prefix} -> ${key}` : key),
    );
  }
  return prefix ? [`${prefix} -> ${String(data)}`] : [String(data)];
};

/**
 * The bare `detail` DRF puts on a refusal (401/403, throttling), if the error
 * carries one. It reads better without the key.
 */
export const liftAPIClientErrorDetail = (error: unknown): string | undefined => {
  if (!(error instanceof LiftAPIClientError)) {
    return undefined;
  }
  const { data } = error.context;
  const detail = data && typeof data === 'object' && 'detail' in data ? data.detail : undefined;
  return typeof detail === 'string' ? detail : undefined;
};

export const formatLiftAPIClientError = (preMessage: string, error: unknown) => {
  if (!(error instanceof LiftAPIClientError)) {
    return preMessage;
  }

  const { status, statusText, data } = error.context;

  let errorMessage = preMessage;

  if (data) {
    const errorMessages =
      liftAPIClientErrorDetail(error) ?? flattenErrorData(data).filter(Boolean).join('\n');

    if (errorMessages) {
      errorMessage = errorMessages;
    }
  }

  return `\n${preMessage}.\n${errorMessage}\n(${status}${statusText ? ` ${statusText}` : ''})`;
};

/**
 * Dotted paths of every field react-hook-form flagged with an error.
 *
 * Walks the `FieldErrors` tree and collects KEYS ONLY — never the `message`,
 * which can embed the value the user submitted (PII). A node counts as a leaf
 * error when it carries a `type`/`message` (RHF's `FieldError` shape).
 */
export const collectFieldErrorPaths = (errors: FieldErrors, prefix = ''): string[] => {
  if (errors == null || typeof errors !== 'object') {
    return [];
  }

  const paths: string[] = [];
  for (const [key, value] of Object.entries(errors)) {
    if (key === 'ref' || value == null) {
      continue;
    }
    const path = prefix ? `${prefix}.${key}` : key;
    const isLeafError =
      typeof value === 'object' && ('message' in value || 'type' in value) && !('length' in value);
    if (isLeafError) {
      paths.push(path);
    } else {
      paths.push(...collectFieldErrorPaths(value as FieldErrors, path));
    }
  }
  return paths;
};

type ShowSnackbar = (message: string, options?: { variant?: 'error' }) => void;

const DEFAULT_HIDDEN_FIELD_MESSAGE =
  "This can't be saved because of a data problem our team needs to fix. It has been reported — please try again later or contact support.";
const DEFAULT_INVALID_MESSAGE = 'Please correct the highlighted fields and try again.';

/**
 * Build a react-hook-form `onInvalid` handler that never fails silently.
 *
 * When `zodResolver` (or any RHF validation) rejects a submit, RHF blocks it
 * with no network request. If the offending field is not rendered with an error
 * binding, the user sees NOTHING — the AMP-585 "Edit Ticket → Save does nothing"
 * incident. This handler is the app-wide guard:
 *
 * 1. Always toasts the user so a blocked submit is never invisible.
 * 2. Reports to Sentry, tagged with `formName` and the flattened failing field
 *    paths (keys only — see {@link collectFieldErrorPaths} on the PII rule), so
 *    we learn which forms/fields block users without waiting for a support call.
 *
 * `isHiddenField` marks paths the form does not render (unfixable by the user).
 * A hidden-field failure is a schema/data bug: it reports at `error` level and
 * gets the distinct message. Everything else is user-correctable (`warning`).
 */
export function createFormInvalidHandler<T extends FieldValues>({
  formName,
  showSnackbar,
  isHiddenField,
  hiddenFieldMessage = DEFAULT_HIDDEN_FIELD_MESSAGE,
  invalidMessage = DEFAULT_INVALID_MESSAGE,
}: {
  formName: string;
  showSnackbar: ShowSnackbar;
  isHiddenField?: (fieldPath: string) => boolean;
  hiddenFieldMessage?: string;
  invalidMessage?: string;
}): SubmitErrorHandler<T> {
  return (errors) => {
    const fieldPaths = collectFieldErrorPaths(errors as FieldErrors);
    const hiddenFieldPaths = isHiddenField ? fieldPaths.filter(isHiddenField) : [];
    const blockedByHiddenField = hiddenFieldPaths.length > 0;

    // Log field PATHS only, never the errors object: `console.error` is captured
    // by Sentry's default Breadcrumbs integration, and the errors tree carries
    // messages (which can embed submitted values) and DOM refs.
    console.error(`${formName} submit blocked by validation on:`, fieldPaths);

    captureMessage(`Form submit blocked by validation: ${formName}`, {
      level: blockedByHiddenField ? 'error' : 'warning',
      fingerprint: [
        'form-invalid',
        formName,
        ...(blockedByHiddenField ? hiddenFieldPaths : fieldPaths).slice().sort(),
      ],
      tags: {
        'form.name': formName,
        'form.blocked_by_hidden_field': String(blockedByHiddenField),
      },
      extra: { formName, fieldPaths, hiddenFieldPaths },
    });

    showSnackbar(blockedByHiddenField ? hiddenFieldMessage : invalidMessage, { variant: 'error' });
  };
}

interface TextFieldDef extends FieldDefBase<TextFieldProps> {
  type:
    | 'text'
    | 'datetime-local'
    | 'date'
    | 'number'
    | 'time'
    | 'text-multiline'
    | 'line-items'
    | 'currency';
  label?: string;
  endAdornment?: string;
}

interface SelectFieldDef extends FieldDefBase<SelectProps> {
  type: 'select' | 'multiple-select';
  options: FieldOption[];
  label?: string;
}

// TODO: Add support for autocomplete props
interface AutocompleteFieldDef extends FieldDefBase<TextFieldProps> {
  type: 'autocomplete' | 'autocomplete-multiple';
  options: FieldOption[];
  loading?: boolean;
  label: string;
  onInputChange?: (value: string) => void;
}

interface CheckboxFieldDef extends FieldDefBase<CheckboxProps> {
  type: 'checkbox';
  label: string;
}

interface SwitchFieldDef extends FieldDefBase<SwitchProps> {
  type: 'switch';
  label: string;
}

interface SpacerFieldDef extends FieldDefBase {
  type: 'spacer';
}

interface CustomFieldDef extends FieldDefBase {
  type: 'custom';
  element: React.ReactNode;
}

export type FieldDef =
  | TextFieldDef
  | SelectFieldDef
  | AutocompleteFieldDef
  | SpacerFieldDef
  | CheckboxFieldDef
  | SwitchFieldDef
  | CustomFieldDef;

/** COMMENT: Mask the input to HH:MM
 *  as MUI TimePicker is not currently stable.
 *  TODO: Extend it to where it limits to 23:59
 */
function maskString(value: string) {
  if (validateMaskHHmm(value)) {
    return value;
  }

  const onlyDigits = value.replace(/[^0-9]/g, '');
  if (onlyDigits.length > 5) {
    return onlyDigits.slice(0, 5);
  }

  if (onlyDigits.length === 4) {
    return `${onlyDigits.slice(0, 2)}:${onlyDigits.slice(2, 4)}`;
  }

  if (onlyDigits.length === 3) {
    return `0${onlyDigits.slice(0, 1)}:${onlyDigits.slice(1, 3)}`;
  }

  if (onlyDigits.length === 2) {
    return `00:${onlyDigits}`;
  }

  if (onlyDigits.length === 1) {
    return `00:0${onlyDigits}`;
  }
}

const Field = <T extends FieldValues>({
  field,
  control,
}: {
  field: FieldDef;
  control: Control<T>;
}) => {
  // Default to required if not specified
  const required = typeof field.required === 'undefined' ? true : field.required;
  const disabled = typeof field.disabled === 'undefined' ? false : field.disabled;

  switch (field.type) {
    case 'text':
    case 'text-multiline':
    case 'currency':
    case 'number':
    case 'datetime-local':
    case 'date': {
      return (
        <Controller
          key={field.name}
          name={field.name as Path<T>}
          control={control}
          render={({ field: { onBlur, onChange, value }, fieldState: { error } }) => {
            return (
              <TextField
                name={field.name}
                type={field.type === 'currency' ? 'number' : field.type}
                value={value ?? ''}
                onBlur={onBlur}
                onChange={
                  field.type === 'number' || field.type === 'currency'
                    ? (e) =>
                        onChange(
                          e.target.value !== '' ? parseFloat(e.target.value) : e.target.value,
                        )
                    : onChange
                }
                label={field.label}
                required={required}
                disabled={disabled}
                {...(field.type === 'text-multiline' ? { multiline: true, rows: 5 } : {})}
                fullWidth
                size="small"
                slotProps={{
                  inputLabel:
                    field.type === 'date' || field.type === 'datetime-local'
                      ? { shrink: true }
                      : {},
                }}
                {...(field.type === 'currency'
                  ? {
                      inputProps: {
                        min: 0,
                        step: 0.01,
                        role: 'textbox',
                      },
                      InputProps: {
                        startAdornment: (
                          <InputAdornment position="start">
                            <CurrencySymbol />
                          </InputAdornment>
                        ),
                      },
                    }
                  : field.endAdornment
                    ? {
                        inputProps: {
                          role: 'textbox',
                        },
                        InputProps: {
                          endAdornment: (
                            <InputAdornment position="end">{field.endAdornment}</InputAdornment>
                          ),
                        },
                      }
                    : {
                        inputProps: {
                          role: 'textbox',
                        },
                      })}
                error={typeof error !== 'undefined'}
                helperText={error?.message}
                {...field.fieldProps}
                defaultValue={
                  field.type === 'currency' ? undefined : field.fieldProps?.defaultValue
                }
              />
            );
          }}
        />
      );
    }

    case 'time': {
      return (
        <Controller
          key={field.name}
          name={field.name as Path<T>}
          control={control}
          render={({ field: { onBlur, onChange, value }, fieldState: { error } }) => {
            return (
              // CAUTION: Don't try use replace this with TimePicker, too many bugs we encountered around validaiton
              // may be fixed in MUI-X 6.0
              <TextField
                name={field.name}
                type={'text'}
                value={value ?? ''}
                onBlur={() => {
                  if (isEmpty(value)) {
                    onChange('');
                    onBlur();
                    return;
                  }
                  const finalVal = maskString(value);
                  onChange(finalVal);
                  onBlur();
                }}
                onChange={(ev) => {
                  onChange(ev);
                }}
                label={field.label}
                required={required}
                disabled={disabled}
                fullWidth
                size="small"
                error={typeof error !== 'undefined'}
                helperText={error?.message}
                placeholder="hh:mm"
              />
            );
          }}
        />
      );
    }
    case 'autocomplete': {
      const optionLabels = new Map(field.options.map(({ id, label }) => [id, label]));
      return (
        <Controller
          key={field.name}
          name={field.name as Path<T>}
          control={control}
          render={({ field: { onBlur, onChange, value }, fieldState: { error } }) => {
            return (
              <Autocomplete
                value={typeof value === 'number' ? value.toString() : value ? value : ''}
                onBlur={onBlur}
                onChange={(event, option) => {
                  onChange(option);
                }}
                onInputChange={(_, value) => {
                  if (field.onInputChange) {
                    field.onInputChange(value);
                  }
                }}
                noOptionsText="No options"
                loading={field.loading}
                renderInput={(props) => (
                  <TextField
                    {...props}
                    value={value ?? ''}
                    label={field.label}
                    name={field.name}
                    required={required}
                    error={typeof error !== 'undefined'}
                    helperText={error?.message}
                  />
                )}
                disabled={disabled}
                fullWidth
                options={field.loading ? [] : field.options.map(({ id }) => id)}
                getOptionLabel={(id) => (field.loading ? '' : (optionLabels.get(id) ?? ''))}
                size="small"
              />
            );
          }}
        />
      );
    }

    case 'autocomplete-multiple': {
      const SELECT_ALL = '__SELECT_ALL__';
      const optionLabels = new Map(field.options.map(({ id, label }) => [id, label]));
      const allOptionIds = field.loading ? [] : field.options.map(({ id }) => id);
      return (
        <Controller
          key={field.name}
          name={field.name as Path<T>}
          control={control}
          render={({ field: { onBlur, onChange, value }, fieldState: { error } }) => {
            const selected: string[] = value ?? [];
            const allSelected =
              allOptionIds.length > 0 && allOptionIds.every((id) => selected.includes(id));
            return (
              <Autocomplete
                value={selected}
                onBlur={onBlur}
                onChange={(event, newValue) => {
                  if (newValue.includes(SELECT_ALL)) {
                    onChange(allSelected ? [] : allOptionIds);
                  } else {
                    onChange(newValue);
                  }
                }}
                onInputChange={(_, value) => {
                  if (field.onInputChange) {
                    field.onInputChange(value);
                  }
                }}
                noOptionsText="No options"
                loading={field.loading}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label={field.label}
                    name={field.name}
                    error={typeof error !== 'undefined'}
                    helperText={error?.message}
                  />
                )}
                disabled={disabled}
                fullWidth
                multiple
                options={field.loading ? [] : [SELECT_ALL, ...allOptionIds]}
                getOptionLabel={(id) => {
                  if (id === SELECT_ALL) return 'Select All';
                  return field.loading ? '' : (optionLabels.get(id) ?? '');
                }}
                size="small"
                disableCloseOnSelect
                renderOption={(props, id, { selected: isSelected }) => {
                  const isSelectAll = id === SELECT_ALL;
                  const liStyle = isSelectAll
                    ? {
                        ...(props as React.HTMLAttributes<HTMLLIElement>).style,
                        borderBottom: '1px solid #e0e0e0',
                      }
                    : (props as React.HTMLAttributes<HTMLLIElement>).style;
                  return (
                    <li {...props} style={liStyle}>
                      <Checkbox
                        name={field.name}
                        icon={<CheckBoxOutlineBlankIcon fontSize="small" />}
                        checkedIcon={<CheckBoxIcon fontSize="small" />}
                        style={{ marginRight: 8 }}
                        checked={isSelectAll ? allSelected : isSelected}
                        indeterminate={isSelectAll && !allSelected ? selected.length > 0 : false}
                        value={id}
                      />
                      {isSelectAll ? 'Select All' : (optionLabels.get(id) ?? '')}
                    </li>
                  );
                }}
                filterOptions={(options, state) => {
                  const filtered = options.filter((id) => {
                    if (id === SELECT_ALL) return true;
                    const label = optionLabels.get(id) ?? '';
                    return label.toLowerCase().includes(state.inputValue.toLowerCase());
                  });
                  return filtered;
                }}
              />
            );
          }}
        />
      );
    }
    case 'select':
    case 'multiple-select': {
      return (
        <Controller
          key={field.name}
          name={field.name as Path<T>}
          control={control}
          render={({ field: { onBlur, onChange, value }, fieldState: { error } }) => {
            return (
              <FormControl fullWidth size="small" required={required} disabled={disabled}>
                <InputLabel id={`input-${field.name}-label`}>{field.label} </InputLabel>
                <Select
                  name={field.name}
                  value={value ?? (field.type === 'select' ? '' : [])}
                  onBlur={onBlur}
                  onChange={onChange}
                  labelId={`input-${field.name}-label`}
                  id={`input-${field.name}`}
                  label={field.label}
                  error={typeof error !== 'undefined'}
                  fullWidth
                  multiple={field.type === 'multiple-select'}
                  {...field.fieldProps}
                >
                  {field.options.map((option) => (
                    <MenuItem key={option.id} value={option.id}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
                {typeof error !== 'undefined' ? (
                  <FormHelperText>{error.message}</FormHelperText>
                ) : null}
              </FormControl>
            );
          }}
        />
      );
    }
    case 'checkbox': {
      return (
        <Controller
          key={field.name}
          name={field.name as Path<T>}
          control={control}
          // TODO figure out how to add error and helper text. Currently we don't have required checkboxes
          render={({ field: { onChange, value }, fieldState: { error } }) => {
            return (
              <FormControlLabel
                label={field.label}
                control={
                  <Checkbox
                    name={field.name}
                    checked={value}
                    onChange={onChange}
                    disabled={disabled}
                    value={value}
                    {...field.fieldProps}
                  />
                }
              />
            );
          }}
        />
      );
    }
    case 'switch': {
      return (
        <Controller
          name={field.name as Path<T>}
          control={control}
          render={({ field: { onChange, value } }) => (
            <Switch
              checked={value ?? false}
              onChange={onChange}
              disabled={disabled}
              {...field.fieldProps}
            />
          )}
        />
      );
    }
    case 'spacer': {
      return null;
    }
    case 'custom': {
      return field.element;
    }
    // TODO: Make this a custom field
    case 'line-items': {
      return (
        <Controller
          key={field.name}
          name={field.name as Path<T>}
          control={control}
          render={({ fieldState: { error } }) => {
            return (
              <>
                <LineItems fieldDef={field} control={control} />
                <Typography variant="caption" color="error">
                  {error?.message}
                </Typography>
              </>
            );
          }}
        />
      );
    }
  }
};

export const FieldAndCell = <T extends FieldValues>({
  field,
  control,
}: {
  field: FieldDef;
  control: Control<T>;
}) => {
  return field.type === 'spacer' ? (
    <Grid size={{ xs: 6 }} />
  ) : (
    <Grid size={{ ...(field.gridProps ?? { xs: 12, sm: 6 }) }}>
      <Field field={field} control={control} />
    </Grid>
  );
};
